// hooks/useTenantPlan.js
import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTenantPlan } from '../redux/allAction/tenantPlanActions';
import { 
  getTenantPlanData,
  getTenantPlanType,
  getTenantPlanLoading,
  getTenantPlanError,
  getPlanFeatures
} from '../redux/selectors/tenantPlanSelectors';

export const useTenantPlan = (options = {}) => {
  const { 
    forceRefresh = false, 
    skipAutoFetch = false,
    autoFetch = true  
  } = options;
  
  const dispatch = useDispatch();
  
  const data = useSelector(getTenantPlanData);
  const plan = useSelector(getTenantPlanType);
  const loading = useSelector(getTenantPlanLoading);
  const error = useSelector(getTenantPlanError);
  const features = useSelector(getPlanFeatures);

  const refetch = useCallback(() => {
    dispatch(fetchTenantPlan());
  }, [dispatch]);

  // Auto-fetch on mount if data doesn't exist
  useEffect(() => {
    if (autoFetch && !skipAutoFetch) {
      if (!data || forceRefresh) {
        refetch();
      }
    }
  }, [data, forceRefresh, refetch, skipAutoFetch, autoFetch]);

  // Memoize the return value to prevent unnecessary rerenders
  const result = useMemo(() => ({
    data,
    plan,
    loading,
    error,
    features,
    refetch,
    isBasic: features.isBasic,
    isPro: features.isPro,
    isEnterprise: features.isEnterprise
  }), [data, plan, loading, error, features, refetch]);

  return result;
};

// Additional specialized hook for just the plan type
export const useTenantPlanType = () => {
  return useSelector(getTenantPlanType);
};

// Additional specialized hook for plan features
export const usePlanFeatures = () => {
  return useSelector(getPlanFeatures);
};